import React, { useCallback, useMemo, useState } from 'react';
import { Input, Button, message, Icon, Radio, Tooltip, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  MUTATION_CANCEL_PAYOUT,
  QUERY_PARTNER_PAYOUT,
  QUERY_SETTLEMENT_INFO,
  QUERY_OPPS_DETAIL,
} from '~pages/dashboard/referral/payout/queries';
import classNames from 'classnames';
import { Statuses } from '~pages/dashboard/referral/payout/list';
import { getSafeHtml } from 'common/helpers/safeHtml';

function PayoutCancelReasonEditor({ bookingId, bookingJourney, onClose }) {
  const { t } = useTranslation();

  const [cancelReason, setCancelReason] = useState('');
  const [deductSettledPayout, setDeductSettledPayout] = useState(null);
  const [validationInfo, setValidateInfo] = useState({ cancelReason: null, deduct: null });

  const { data: payoutListResponse, loading: payoutListLoading } = useQuery(QUERY_OPPS_DETAIL, {
    variables: {
      bookingIds: bookingId,
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const [cancelPayout, { loading: cancelPayoutLoading }] = useMutation(MUTATION_CANCEL_PAYOUT, {
    refetchQueries: [QUERY_PARTNER_PAYOUT, QUERY_SETTLEMENT_INFO],
    onError: e => {
      message.error(e.message);
    },
  });

  const shouldDisabledDeduct = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.edges?.every(
      edge => edge.node.status !== Statuses.Settled,
    );
  }, [payoutListResponse]);

  const handleSubmit = useCallback(async (bookingId, cancelReason, deduct, isDeductDisabled) => {
    if (!cancelReason) {
      return setValidateInfo({
        cancelReason: t('cms.referral.payout.cancel_reason.editor.input.validate'),
      });
    }
    if (typeof deduct !== 'boolean' && !isDeductDisabled) {
      return setValidateInfo({
        deduct: t('cms.referral.payout.cancel_reason.editor.radio.validate'),
      });
    }
    const response = await cancelPayout({
      variables: {
        bookingId: bookingId,
        bookingJourney,
        cancelReason: cancelReason,
        deduct: !!deduct,
        secondaryStatus: deduct ? 'MANUAL_DEDUCT' : 'MANUAL_CANCEL',
      },
    });
    if (response?.data?.cancelPayout?.result) {
      message.success(t('cms.referral.payout.cancel_reason.editor.toast.success'));
      onClose();
    }
  }, []);

  return (
    <>
      <div className="payout-cancel-reason-editor__body">
        <Choose>
          <When condition={payoutListLoading}>
            <div className="payout-cancel-reason-editor__spin">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <section className="payout-cancel-reason-editor__section">
              <main className="payout-cancel-reason-editor__main">
                <div className="payout-cancel-reason-editor__cancel-reason__title ant-legacy-form-item-required">
                  {t('cms.referral.payout.cancel_reason.editor.input.title')}
                </div>
                <div
                  className={classNames('payout-cancel-reason-editor__textarea', {
                    'has-error': validationInfo.cancelReason,
                  })}
                >
                  <Input.TextArea
                    value={cancelReason}
                    onBlur={e =>
                      setCancelReason(e.currentTarget.value && e.currentTarget.value.trim())
                    }
                    onChange={e => {
                      const trimmedValue = e.currentTarget.value && e.currentTarget.value.trim();
                      setCancelReason(e.currentTarget.value);
                      if (trimmedValue) {
                        setValidateInfo({ cancelReason: null });
                      }
                    }}
                    rows={2}
                    maxLength={20}
                  />
                  <div className="payout-cancel-reason-editor__textarea__info">
                    <div>
                      {validationInfo.cancelReason ? (
                        <div className="ant-legacy-form-explain">{validationInfo.cancelReason}</div>
                      ) : null}
                    </div>
                    <p>{cancelReason?.length || 0} / 20</p>
                  </div>
                </div>
                <div>
                  <div className="payout-cancel-reason-editor__deduct__title ant-legacy-form-item-required">
                    {t('Deduct previous settled payout')}
                    <Tooltip
                      title={
                        <>
                          <div>
                            {getSafeHtml(t('cms.referral.payout.cancel_reason.editor.radio.tips'))}
                          </div>
                        </>
                      }
                      placement="top"
                      className="payout-cancel-reason-editor__deduct__tip"
                    >
                      <QuestionCircleOutlined className="payout-cancel-reason-editor__deduct__tip-icon" />
                    </Tooltip>
                  </div>
                  <div
                    className={classNames('payout-cancel-reason-editor__radio', {
                      'has-error': validationInfo.deduct,
                    })}
                  >
                    <Radio.Group
                      onChange={e => setDeductSettledPayout(e.target.value)}
                      value={deductSettledPayout}
                      disabled={shouldDisabledDeduct}
                    >
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </div>
                  <div className="payout-cancel-reason-editor__validation-error">
                    <div>{validationInfo.deduct ? <div>{validationInfo.deduct}</div> : null}</div>
                  </div>
                </div>

                <div className="payout-cancel-reason-editor__tips">
                  <div className="payout-cancel-reason-editor__tips__icon">
                    <Icon
                      type="info-circle"
                      theme="filled"
                      width="14"
                      height={14}
                      style={{ color: '#F9A600', fontSize: '14px' }}
                    />
                  </div>
                  {t('cms.referral.payout.cancel_reason.editor.tips')}
                </div>
              </main>
            </section>
          </Otherwise>
        </Choose>
      </div>
      <div className="payout-cancel-reason-editor__footer">
        <Button
          size="large"
          className="payout-cancel-reason-editor__cancel-button"
          onClick={onClose}
        >
          {t('cms.referral.payout.cancel_reason.editor.btn.no')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="payout-cancel-reason-editor__save-button"
          loading={cancelPayoutLoading}
          onClick={() =>
            handleSubmit(bookingId, cancelReason, deductSettledPayout, shouldDisabledDeduct)
          }
        >
          {t('cms.referral.payout.cancel_reason.editor.btn.yes')}
        </Button>
      </div>
    </>
  );
}

export default PayoutCancelReasonEditor;
